import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { Person } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { showFieldErrors } from '../../shared/ui/fieldErrors';
import { PersonDetailsFields, readPersonDetails } from '../../shared/ui/PersonDetailsFields';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { formatDay } from './AdminChurchesPage';

export interface AdminUser {
  accountId: string;
  personId: string;
  name: string;
  givenName: string;
  familyName: string;
  birthDate: string | null;
  gender: string | null;
  city: string | null;
  phone: string | null;
  email: string;
  status: 'active' | 'disabled';
  createdAt: string;
  emailVerified: boolean;
  lastLoginAt: string | null;
  openSessions: number;
  churches: { id: string; name: string; status: 'pending' | 'active'; roles: string[] }[];
  family: string | null;
  platformAdmin: boolean;
}

const NO_CHURCH = 'ninguna';
const DAY = 86_400_000;
const SINCE = { todos: null, hoy: 1, '7': 7, '30': 30, anio: 'year' } as const;
type Since = keyof typeof SINCE;
const AGES = { todas: null, ninos: [0, 12], adolescentes: [13, 17], jovenes: [18, 29], adultos: [30, 59], mayores: [60, 200], sin: 'none' } as const;
type Ages = keyof typeof AGES;
const STATES = {
  todos: 'Todos',
  sin_verificar: 'Correo sin confirmar',
  duplicados: 'Sin confirmar con otra cuenta del mismo nombre',
  nunca: 'Nunca ha entrado',
  sin_iglesia: 'Sin iglesia',
  sin_familia: 'Sin familia',
  desactivadas: 'Cuentas desactivadas',
} as const;
type State = keyof typeof STATES;

function age(birthDate: string | null, today = new Date()): number | null {
  if (!birthDate) return null;
  const [y, m, d] = birthDate.split('-').map(Number) as [number, number, number];
  return today.getFullYear() - y - (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d) ? 1 : 0);
}

const plain = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const firstWord = (t: string) => plain(t).trim().split(/\s+/)[0] ?? '';
/** Misma persona probable: mismo primer nombre y mismo primer apellido. */
const nameKey = (u: AdminUser) => `${firstWord(u.givenName)}|${firstWord(u.familyName)}`;
/** Para cada cuenta sin confirmar, las otras cuentas con el mismo nombre (las confirmadas primero). */
function twinsOf(users: AdminUser[]): Map<string, AdminUser[]> {
  const byName = new Map<string, AdminUser[]>();
  for (const u of users) byName.set(nameKey(u), [...(byName.get(nameKey(u)) ?? []), u]);
  const twins = new Map<string, AdminUser[]>();
  for (const u of users) {
    if (u.emailVerified) continue;
    const others = (byName.get(nameKey(u)) ?? []).filter((o) => o.personId !== u.personId);
    if (others.length) twins.set(u.personId, others.sort((a, b) => Number(b.emailVerified) - Number(a.emailVerified)));
  }
  return twins;
}

function matches(u: AdminUser, f: { q: string; church: string; since: Since; ages: Ages; gender: string; state: State; twins: Map<string, AdminUser[]> }): boolean {
  if (f.q) {
    const q = plain(f.q);
    if (![u.name, u.email, u.phone ?? ''].some((v) => plain(v).includes(q))) return false;
  }
  if (f.church === NO_CHURCH ? u.churches.length > 0 : f.church && !u.churches.some((c) => c.id === f.church)) return false;
  const since = SINCE[f.since];
  if (since === 'year' && new Date(u.createdAt).getFullYear() !== new Date().getFullYear()) return false;
  if (typeof since === 'number' && Date.now() - new Date(u.createdAt).getTime() > since * DAY) return false;
  const range = AGES[f.ages];
  const years = age(u.birthDate);
  if (range === 'none' && years !== null) return false;
  if (Array.isArray(range) && (years === null || years < range[0] || years > range[1])) return false;
  if (f.gender && u.gender !== f.gender) return false;
  if (f.state === 'sin_verificar' && u.emailVerified) return false;
  if (f.state === 'duplicados' && !f.twins.has(u.personId)) return false;
  if (f.state === 'nunca' && u.lastLoginAt) return false;
  if (f.state === 'sin_iglesia' && u.churches.length > 0) return false;
  if (f.state === 'sin_familia' && u.family) return false;
  if (f.state === 'desactivadas' && u.status !== 'disabled') return false;
  return true;
}

function exportCsv(users: AdminUser[]) {
  const cell = (v: string | number | null) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = [
    ['Nombre', 'Correo', 'Correo confirmado', 'Celular', 'Nacimiento', 'Edad', 'Sexo', 'Ciudad', 'Iglesias', 'Familia', 'Registro', 'Último ingreso', 'Estado'],
    ...users.map((u) => [
      u.name, u.email, u.emailVerified ? 'Sí' : 'No', u.phone, u.birthDate, age(u.birthDate), u.gender, u.city,
      u.churches.map((c) => c.name).join('; '), u.family, u.createdAt.slice(0, 10), u.lastLoginAt?.slice(0, 10) ?? 'Nunca', u.status === 'active' ? 'Activa' : 'Desactivada',
    ]),
  ];
  const blob = new Blob(['﻿' + rows.map((r) => r.map(cell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `usuarios-keora-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
}

/** Panel de KEORA > Usuarios: todas las cuentas, con filtros y lo necesario para atenderlas (solo Superadmin). */
export function AdminUsersPage() {
  const me = useMe();
  const users = useAsync((signal) => api.get<{ items: AdminUser[] }>('/multichurch/admin/users', signal).then((r) => r.items), []);
  const [q, setQ] = useState('');
  const [church, setChurch] = useState('');
  const [since, setSince] = useState<Since>('todos');
  const [ages, setAges] = useState<Ages>('todas');
  const [gender, setGender] = useState('');
  const [state, setState] = useState<State>('todos');
  const [open, setOpen] = useState<string | null>(null);
  const churchList = useAsync(
    (signal) => api.get<{ items: ChurchOption[] }>('/multichurch/admin/churches', signal).then((r) => r.items.filter((c) => c.status === 'active').sort((a, b) => a.name.localeCompare(b.name, 'es'))),
    [],
  );
  const options = churchList.status === 'ready' ? churchList.data : [];
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  if (!me.isPlatformAdmin) return <Navigate to="/app" replace />;

  const all = users.status === 'ready' ? users.data : [];
  const churches = [...new Map(all.flatMap((u) => u.churches).map((c) => [c.id, c.name])).entries()].sort((a, b) => a[1].localeCompare(b[1], 'es'));
  const twins = twinsOf(all);
  const shown = all.filter((u) => matches(u, { q: q.trim(), church, since, ages, gender, state, twins }));

  return (
    <div className="page">
      <header className="page__header">
        <h1>Usuarios</h1>
        <p className="muted">Todas las cuentas de KEORA. Solo tú, como administración de KEORA, ves esta página; cada cambio queda en la auditoría.</p>
      </header>
      {users.status === 'ready' ? (
        <ul className="admin-stats" aria-label="Resumen">
          <li><strong>{all.length}</strong> cuentas</li>
          <li><strong>{all.filter((u) => !u.emailVerified).length}</strong> sin confirmar correo</li>
          <li><strong>{all.filter((u) => u.churches.length === 0).length}</strong> sin iglesia</li>
          <li><strong>{all.filter((u) => !u.lastLoginAt).length}</strong> nunca han entrado</li>
          <li><strong>{twins.size}</strong> sin confirmar con otra cuenta del mismo nombre</li>
        </ul>
      ) : null}

      <Card>
        <div className="form">
          <Field label="Buscar por nombre, correo o celular" name="q" type="search" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="form__row">
            <div className="field">
              <label htmlFor="u-church">Iglesia</label>
              <select id="u-church" value={church} onChange={(e) => setChurch(e.target.value)}>
                <option value="">Todas</option>
                <option value={NO_CHURCH}>Sin iglesia</option>
                {churches.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="u-state">Situación</label>
              <select id="u-state" value={state} onChange={(e) => setState(e.target.value as State)}>
                {(Object.keys(STATES) as State[]).map((k) => (
                  <option key={k} value={k}>{STATES[k]}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form__row">
            <div className="field">
              <label htmlFor="u-since">Se registró</label>
              <select id="u-since" value={since} onChange={(e) => setSince(e.target.value as Since)}>
                <option value="todos">Cuando sea</option>
                <option value="hoy">Hoy</option>
                <option value="7">Últimos 7 días</option>
                <option value="30">Últimos 30 días</option>
                <option value="anio">Este año</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="u-ages">Edad</label>
              <select id="u-ages" value={ages} onChange={(e) => setAges(e.target.value as Ages)}>
                <option value="todas">Todas</option>
                <option value="ninos">Niños (0 a 12)</option>
                <option value="adolescentes">Adolescentes (13 a 17)</option>
                <option value="jovenes">Jóvenes (18 a 29)</option>
                <option value="adultos">Adultos (30 a 59)</option>
                <option value="mayores">60 o más</option>
                <option value="sin">Sin fecha de nacimiento</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="u-gender">Sexo</label>
              <select id="u-gender" value={gender} onChange={(e) => setGender(e.target.value)}>
                <option value="">Todos</option>
                <option value="femenino">Femenino</option>
                <option value="masculino">Masculino</option>
              </select>
            </div>
          </div>
          <div className="actions actions--wrap">
            <span className="muted">{shown.length === 1 ? '1 cuenta' : `${shown.length} cuentas`}</span>
            <Button type="button" variant="secondary" disabled={shown.length === 0} onClick={() => exportCsv(shown)}>
              Exportar lista
            </Button>
            <Button type="button" variant="ghost" disabled={shown.length === 0} onClick={() => setPicked(new Set(shown.slice(0, 300).map((u) => u.personId)))}>
              Seleccionar las que se ven
            </Button>
          </div>
        </div>
      </Card>

      {picked.size > 0 ? (
        <BulkChurch
          users={all.filter((u) => picked.has(u.personId))}
          options={options}
          onClear={() => setPicked(new Set())}
          onDone={users.reload}
        />
      ) : null}

      {users.status === 'loading' ? <Loading /> : null}
      {users.status === 'error' ? <ErrorState error={users.error} onRetry={users.reload} /> : null}
      {users.status === 'ready' && shown.length === 0 ? <EmptyState title="Ninguna cuenta con esos filtros." /> : null}
      <ul className="list">
        {shown.slice(0, 300).map((u) => (
          <li key={u.personId} className="list__item">
            <input type="checkbox" aria-label={`Seleccionar a ${u.name}`} checked={picked.has(u.personId)} onChange={() => toggle(u.personId)} style={{ marginTop: '0.3rem' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <button type="button" className="link-button" onClick={() => setOpen(open === u.personId ? null : u.personId)} aria-expanded={open === u.personId}>
                <strong>{u.name}</strong>
              </button>
              <div className="muted small">
                {[
                  u.email,
                  u.emailVerified ? null : 'correo sin confirmar',
                  u.churches.length ? u.churches.map((c) => c.name).join(', ') : 'sin iglesia',
                  age(u.birthDate) !== null ? `${age(u.birthDate)} años` : null,
                  `se registró ${formatDay(u.createdAt)}`,
                  u.lastLoginAt ? null : 'nunca ha entrado',
                  u.status === 'disabled' ? 'desactivada' : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              {twins.get(u.personId)?.length ? (
                <p className="small text-error">
                  Posible cuenta repetida. Con el mismo nombre:{' '}
                  {twins.get(u.personId)!.map((o, i) => (
                    <span key={o.personId}>
                      {i ? ', ' : ''}
                      <button type="button" className="link-button" onClick={() => { setQ(o.email); setState('todos'); setOpen(o.personId); }}>
                        {o.name}
                      </button>{' '}
                      ({o.email}, {o.emailVerified ? 'correo confirmado' : 'sin confirmar'}{o.birthDate && o.birthDate === u.birthDate ? ', misma fecha de nacimiento' : ''})
                    </span>
                  ))}
                </p>
              ) : null}
              {open === u.personId ? <UserPanel user={u} self={u.personId === me.personId} options={options} onChanged={users.reload} /> : null}
            </div>
          </li>
        ))}
      </ul>
      {shown.length > 300 ? <p className="muted small">Se muestran las primeras 300. Usa los filtros o exporta la lista completa.</p> : null}
    </div>
  );
}

interface ChurchOption {
  id: string;
  name: string;
  status: string;
}

const activeChurch = (u: AdminUser) => u.churches.find((c) => c.status === 'active') ?? null;

function ChurchSelect({ id, options, exclude }: { id: string; options: ChurchOption[]; exclude?: string }) {
  return (
    <div className="field">
      <label htmlFor={id}>Iglesia</label>
      <select id={id} name="churchId" required defaultValue="">
        <option value="" disabled>Elige la iglesia</option>
        {options.filter((c) => c.id !== exclude).map((c) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
    </div>
  );
}

/** Asignar una iglesia a varias cuentas a la vez (por ejemplo, las que están sin iglesia). */
function BulkChurch({ users, options, onClear, onDone }: { users: AdminUser[]; options: ChurchOption[]; onClear: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: number; failed: string[] } | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const churchId = String(new FormData(event.currentTarget).get('churchId') ?? '');
    if (!churchId) return;
    const church = options.find((c) => c.id === churchId)?.name ?? 'esa iglesia';
    const moving = users.filter((u) => activeChurch(u) && activeChurch(u)!.id !== churchId).length;
    if (!window.confirm(`¿Asignar ${church} a ${users.length === 1 ? '1 cuenta' : `${users.length} cuentas`}?${moving ? ` ${moving} ya tienen otra iglesia y se cambiarán.` : ''}`)) return;
    setBusy(true);
    let ok = 0;
    const failed: string[] = [];
    for (const u of users) {
      try {
        await api.put(`/multichurch/admin/users/${u.personId}/church`, { churchId });
        ok += 1;
      } catch (e) {
        failed.push(`${u.name}: ${errorMessage(e)}`);
      }
    }
    setBusy(false);
    setResult({ ok, failed });
    onDone();
  }

  return (
    <Card>
      <form className="form" onSubmit={submit}>
        <p><strong>{users.length === 1 ? '1 cuenta seleccionada' : `${users.length} cuentas seleccionadas`}</strong></p>
        <ChurchSelect id="bulk-church" options={options} />
        <div className="actions actions--wrap">
          <Button type="submit" busy={busy}>Asignar iglesia</Button>
          <Button type="button" variant="ghost" disabled={busy} onClick={() => { setResult(null); onClear(); }}>Quitar selección</Button>
        </div>
        {result && result.ok > 0 ? <Alert tone="success">{result.ok === 1 ? 'Se asignó 1 cuenta.' : `Se asignaron ${result.ok} cuentas.`} Ya aparecen en Miembros de su iglesia.</Alert> : null}
        {result && result.failed.length > 0 ? <Alert>No se pudieron asignar: {result.failed.join(' · ')}</Alert> : null}
      </form>
    </Card>
  );
}

function UserPanel({ user, self, options, onChanged }: { user: AdminUser; self: boolean; options: ChurchOption[]; onChanged: () => void }) {
  const base = `/multichurch/admin/users/${user.personId}`;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<'email' | 'password' | 'details' | 'church' | null>(null);
  const current = activeChurch(user);

  async function run(action: () => Promise<unknown>, done: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(done);
      setEditing(null);
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function onEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const email = String(new FormData(event.currentTarget).get('email') ?? '');
    void run(() => api.put(`${base}/email`, { email }), `Correo cambiado. Le mandamos el enlace para confirmarlo a ${email.trim().toLowerCase()}.`);
  }
  function onPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const password = String(new FormData(event.currentTarget).get('password') ?? '');
    void run(() => api.put(`${base}/password`, { password }), 'Contraseña nueva guardada. Se cerraron sus sesiones; dásela por un medio seguro.');
  }

  function onChurch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const churchId = String(new FormData(event.currentTarget).get('churchId') ?? '');
    const name = options.find((c) => c.id === churchId)?.name ?? 'la iglesia';
    void run(() => api.put(`${base}/church`, { churchId }), `Listo: ya es miembro de ${name} y la iglesia lo ve en Miembros.`);
  }

  return (
    <div className="stack" style={{ marginTop: '0.75rem' }}>
      <dl className="details">
        <dt>Correo</dt>
        <dd>{user.email} {user.emailVerified ? '(confirmado)' : '(sin confirmar)'}</dd>
        <dt>Celular</dt>
        <dd>{user.phone ?? '—'}</dd>
        <dt>Nacimiento</dt>
        <dd>{user.birthDate ? `${formatDay(user.birthDate + 'T12:00:00')}${user.city ? `, ${user.city}` : ''}` : '—'}</dd>
        <dt>Iglesias</dt>
        <dd>
          {user.churches.length
            ? user.churches.map((c) => `${c.name}${c.status === 'pending' ? ' (solicitud pendiente)' : ''}${c.roles.length ? ` · ${c.roles.join(', ')}` : ''}`).join(' | ')
            : 'Ninguna'}
        </dd>
        <dt>Familia</dt>
        <dd>{user.family ?? 'Ninguna'}</dd>
        <dt>Último ingreso</dt>
        <dd>{user.lastLoginAt ? `${formatDay(user.lastLoginAt)} · ${user.openSessions} ${user.openSessions === 1 ? 'sesión abierta' : 'sesiones abiertas'}` : 'Nunca'}</dd>
        <dt>Cuenta</dt>
        <dd>{user.status === 'active' ? 'Activa' : 'Desactivada'}{user.platformAdmin ? ' · administración de KEORA' : ''}</dd>
      </dl>
      {error ? <Alert>{error}</Alert> : null}
      {notice ? <Alert tone="success">{notice}</Alert> : null}
      <div className="actions actions--wrap">
        {!user.emailVerified ? (
          <>
            <Button type="button" busy={busy} onClick={() => void run(() => api.post(`${base}/verify-email`), 'Correo confirmado. Ya puede entrar.')}>
              Confirmar correo
            </Button>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => void run(() => api.post(`${base}/resend-verification`), 'Le reenviamos el correo de confirmación.')}>
              Reenviar confirmación
            </Button>
          </>
        ) : null}
        <Button type="button" variant="secondary" disabled={busy} onClick={() => setEditing(editing === 'email' ? null : 'email')}>
          Cambiar correo
        </Button>
        <Button type="button" variant="secondary" disabled={busy} onClick={() => setEditing(editing === 'password' ? null : 'password')}>
          Poner contraseña
        </Button>
        <Button type="button" variant="secondary" disabled={busy} onClick={() => setEditing(editing === 'details' ? null : 'details')}>
          Editar datos
        </Button>
        <Button type="button" variant="secondary" disabled={busy} onClick={() => setEditing(editing === 'church' ? null : 'church')}>
          {current ? 'Cambiar de iglesia' : 'Asignar iglesia'}
        </Button>
        {current ? (
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={() => window.confirm(`¿Quitar a ${user.name} de ${current.name}? Su historia se conserva.`) && void run(() => api.delete(`${base}/church`), `Ya no es miembro de ${current.name}.`)}
          >
            Quitar de la iglesia
          </Button>
        ) : null}
        {user.openSessions > 0 ? (
          <Button type="button" variant="ghost" disabled={busy} onClick={() => void run(() => api.post(`${base}/end-sessions`), 'Se cerraron sus sesiones.')}>
            Cerrar sesiones
          </Button>
        ) : null}
        {!self ? (
          user.status === 'active' ? (
            <Button
              type="button"
              variant="danger"
              disabled={busy}
              onClick={() => window.confirm(`¿Desactivar la cuenta de ${user.name}? No podrá entrar hasta que la actives.`) && void run(() => api.put(`${base}/status`, { status: 'disabled' }), 'Cuenta desactivada.')}
            >
              Desactivar
            </Button>
          ) : (
            <Button type="button" disabled={busy} onClick={() => void run(() => api.put(`${base}/status`, { status: 'active' }), 'Cuenta activada.')}>
              Activar
            </Button>
          )
        ) : null}
      </div>
      {editing === 'email' ? (
        <form className="form form--inline" onSubmit={onEmail}>
          <Field label="Correo nuevo" name="email" type="email" required autoComplete="off" defaultValue={user.email} />
          <Button type="submit" busy={busy}>Guardar correo</Button>
        </form>
      ) : null}
      {editing === 'password' ? (
        <form className="form form--inline" onSubmit={onPassword}>
          <Field label="Contraseña nueva" name="password" type="text" required minLength={10} autoComplete="new-password" hint="Al menos 10 caracteres. Se cerrarán sus sesiones." />
          <Button type="submit" busy={busy}>Guardar contraseña</Button>
        </form>
      ) : null}
      {editing === 'church' ? (
        <form className="form form--inline" onSubmit={onChurch}>
          <ChurchSelect id={`church-${user.personId}`} options={options} exclude={current?.id} />
          <Button type="submit" busy={busy}>{current ? 'Cambiar' : 'Asignar'}</Button>
          {current ? <p className="muted small">Dejará de ser miembro de {current.name}.</p> : null}
        </form>
      ) : null}
      {editing === 'details' ? <DetailsForm personId={user.personId} onSaved={() => void run(async () => undefined, 'Datos guardados.')} /> : null}
    </div>
  );
}

function DetailsForm({ personId, onSaved }: { personId: string; onSaved: () => void }) {
  const person = useAsync((signal) => api.get<Person>(`/multichurch/admin/users/${personId}/details`, signal), [personId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const { details, error: missing, fields } = readPersonDetails(new FormData(formEl), { requireAll: false });
    if (missing) {
      setError(missing);
      showFieldErrors(formEl, fields);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.put(`/identity/people/${personId}/details`, details);
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (person.status === 'loading') return <Loading />;
  if (person.status === 'error') return <ErrorState error={person.error} onRetry={person.reload} />;
  return (
    <form className="form" onSubmit={submit} noValidate>
      {error ? <Alert>{error}</Alert> : null}
      <PersonDetailsFields idPrefix={`admin-${personId}`} person={person.data} optional />
      <Button type="submit" busy={busy}>Guardar datos</Button>
    </form>
  );
}
